/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { ReactElement, useEffect, useState } from 'react';
import { Popup } from 'react-leaflet';
import { Site } from '../types/Site';
import { submitSiteChangeRequest, submitFibreChangeRequest, fetchSiteEquipment, hasSiteMultipleEquipment } from '../utils/siteUtils';
import { useAuth } from '../contexts/AuthContext';

interface SitePopupProps {
  site: Site;
  onNavigate?: (site: Site) => void;
}

const PopupContainer = styled.div`
  background: #1a1a1a;
  color: #f0f0f0;
  border-radius: 12px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  width: 320px;
  font-family: 'Segoe UI', -apple-system, sans-serif;
  
  /* Remove white surround by overriding Leaflet styles */
  .leaflet-popup-content-wrapper {
    background: none;
    box-shadow: none;
    padding: 0;
    margin: 0;
  }
  
  .leaflet-popup-content {
    margin: 0;
    padding: 0;
  }
  
  .leaflet-popup-tip-container {
    display: none;
  }
`;

const HeaderSection = styled.div<{ siteType?: string }>`
  background: ${props => 
    props.siteType === 'SDH' 
      ? 'linear-gradient(135deg, #1e88e5, #1565c0)'
      : props.siteType === 'PDH'
      ? 'linear-gradient(135deg, #7b1fa2, #6a1b9a)'
      : (props.siteType === 'Signal' || props.siteType === 'Sub Signal')
      ? 'linear-gradient(135deg, #d32f2f, #b71c1c)'
      : props.siteType === 'Points'
      ? 'linear-gradient(135deg, #1e88e5, #1565c0)'
      : props.siteType === 'Fibre'
      ? 'linear-gradient(135deg, #00bcd4, #0097a7)'
      : 'linear-gradient(135deg, #ff8c00, #e67e00)'
  };
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
`;

const SiteName = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: white;
  letter-spacing: 0.01em;
`;

const SiteType = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
  margin-top: 4px;
`;

const BodySection = styled.div`
  padding: 16px 20px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px 20px;
  margin-bottom: 16px;
`;

const InfoLabel = styled.div`
  color: #999;
  font-size: 13px;
  font-weight: 500;
  align-self: start;
`;

const InfoValue = styled.div`
  color: #f0f0f0;
  font-size: 14px;
  align-self: start;
  word-break: break-word;
`;

const AccessContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const SuggestAccessButton = styled.button`
  background: none;
  border: none;
  color: #ff8c00;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
  margin-left: auto;
  
  &:hover {
    background-color: rgba(255, 140, 0, 0.2);
  }
  
  &::before {
    content: '💡';
    font-size: 14px;
  }
`;

const _W3WLink = styled.a`
  color: #ff8c00;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
  font-weight: 500;
  
  &:hover {
    color: #ffae4d;
    text-decoration: underline;
  }
  
  &::before {
    content: '';
    display: inline-block;
    width: 16px;
    height: 16px;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ff8c00"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>');
    background-repeat: no-repeat;
    background-size: contain;
    margin-right: 6px;
  }
`;

const CoordinatesBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  padding: 4px 8px;
  margin-top: 8px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  cursor: pointer;
  user-select: all;
  transition: background-color 0.2s;
  color: #ccc;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
  
  &.copied {
    background: rgba(40, 167, 69, 0.3);
    color: #4ade80;
  }
`;

const CopyIcon = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 6px;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>');
  background-repeat: no-repeat;
  background-size: contain;
`;

const CopySuccessIcon = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 6px;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%234ade80"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>');
  background-repeat: no-repeat;
  background-size: contain;
`;

const NavigateButtonContainer = styled.div`
  margin-top: 16px;
`;

const NavigateButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  
  &:hover {
    background-color: #0069d9;
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(0, 123, 255, 0.4);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
  
  &::before {
    content: '🚙';  /* Alternatives: 🚗 (car), 🚙 (SUV), 🚐 (van) */
    display: inline-block;
    font-size: 18px;
    margin-right: 8px;
  }
`;

const ActionsSection = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: 8px 0 12px;
  width: 100%;
`;

const ActionButton = styled.button`
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  border: none;
  background-color: #3b82f6;
  color: white;
  transition: all 0.2s ease;
  width: auto;
  align-self: flex-end;
  margin-right: 4px;
  box-shadow: 0 2px 5px rgba(59, 130, 246, 0.3);
  
  &:hover {
    background-color: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(59, 130, 246, 0.4);
  }
  
  &:active {
    transform: scale(0.98);
    box-shadow: 0 1px 3px rgba(59, 130, 246, 0.2);
  }
`;

const SuggestButton = styled.button`
  background-color: #ff8c00;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80%;
  
  &:hover {
    background-color: #ffae4d;
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(255, 140, 0, 0.4);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
  
  &::before {
    content: '';
    display: inline-block;
    width: 14px;
    height: 14px;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>');
    background-repeat: no-repeat;
    background-size: contain;
    margin-right: 8px;
  }
`;

const AccessSuggestionForm = styled.div`
  margin-top: 16px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 1000;
  width: 100%;
  box-sizing: border-box;
  
  /* Prevent click propagation to parent elements */
  * {
    pointer-events: auto;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  color: #f0f0f0;
  resize: vertical;
  margin-bottom: 12px;
  position: relative;
  z-index: 1001;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: rgba(255, 140, 0, 0.6);
    box-shadow: 0 0 0 2px rgba(255, 140, 0, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const FormButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const FormButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 0 0 45%;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.variant === 'secondary' ? 'rgba(255, 255, 255, 0.1)' : '#3b82f6'};
  color: white;
  position: relative;
  z-index: 1002;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.variant === 'secondary' ? 'rgba(255, 255, 255, 0.15)' : '#2563eb'};
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(59, 130, 246, 0.4);
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: none;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  display: flex;
  align-items: center;
  background-color: ${props => props.type === 'success' ? 'rgba(40, 167, 69, 0.2)' : 'rgba(220, 53, 69, 0.2)'};
  color: ${props => props.type === 'success' ? '#4ade80' : '#f87171'};
  
  &::before {
    content: '';
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 8px;
    background-image: url(${props => props.type === 'success' 
      ? 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%234ade80"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>'
      : 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23f87171"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>'});
    background-repeat: no-repeat;
    background-size: contain;
  }
`;

const EquipmentButton = styled.button<{ expanded?: boolean }>`
  background: rgba(59, 130, 246, 0.1);
  border: none;
  border-radius: 4px;
  color: #3b82f6;
  font-size: 13px;
  padding: 6px 10px;
  margin-top: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(59, 130, 246, 0.2);
    color: #2563eb;
  }
  
  &::before {
    content: '';
    display: inline-block;
    width: 12px;
    height: 12px;
    margin-right: 6px;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%233b82f6"><path d="M7 10l5 5 5-5z"/></svg>');
    background-repeat: no-repeat;
    background-size: contain;
    transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0.2s ease;
  }
`;

const EquipmentList = styled.div`
  margin-top: 10px;
  padding: 10px;
  background: rgba(59, 130, 246, 0.05);
  border-left: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: thin;
  font-size: 13px;
  width: 100%;
  box-sizing: border-box;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(59, 130, 246, 0.4);
    border-radius: 3px;
  }
`;

const EquipmentItem = styled.div`
  padding: 6px 0;
  display: flex;
  align-items: flex-start;
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
  
  &::before {
    content: '';
    display: inline-block;
    min-width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #3b82f6;
    margin-right: 8px;
    margin-top: 6px;
  }
`;

// Add a loading indicator styled component
const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  color: #3b82f6;
  font-size: 14px;
  
  &::before {
    content: '';
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 8px;
    border: 2px solid #3b82f6;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Add styles for collapsible sections
const CollapsibleSectionButton = styled.button<{ expanded?: boolean }>`
  background: rgba(59, 130, 246, 0.1);
  border: none;
  border-radius: 4px;
  color: #3b82f6;
  font-size: 13px;
  padding: 8px 10px;
  margin: 8px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  width: 100%;
  text-align: left;
  
  &:hover {
    background: rgba(59, 130, 246, 0.2);
    color: #2563eb;
  }
  
  &::before {
    content: '';
    display: inline-block;
    width: 12px;
    height: 12px;
    margin-right: 6px;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%233b82f6"><path d="M7 10l5 5 5-5z"/></svg>');
    background-repeat: no-repeat;
    background-size: contain;
    transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0.2s ease;
  }
`;

const CollapsibleContent = styled.div<{ expanded?: boolean }>`
  display: ${props => props.expanded ? 'block' : 'none'};
  animation: ${props => props.expanded ? 'fadeIn 0.3s ease' : 'none'};
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

// Simple mobile device detection function
const isMobileDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  
  // Check for standard mobile devices
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i;
  
  // Also check for touch capability which is present on mobile devices but add a width check
  // to distinguish between touch-enabled desktops and actual mobile devices
  const hasTouchCapability = 'ontouchstart' in window || 
    navigator.maxTouchPoints > 0 || 
    (navigator as any).msMaxTouchPoints > 0;
  
  const viewportWidth = window.innerWidth;
  const isNarrowScreen = viewportWidth < 768; // Common breakpoint for mobile
  
  const result = mobileRegex.test(navigator.userAgent) || (hasTouchCapability && isNarrowScreen);
  console.log(`isMobileDevice detection - UA: ${navigator.userAgent}, Result: ${result}`);
  return result;
};

// Detect iOS specifically
const isIOSDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  
  return !!(
    navigator.userAgent.match(/iPhone/i) ||
    navigator.userAgent.match(/iPad/i) ||
    navigator.userAgent.match(/iPod/i)
  );
};

export const SitePopup = ({ site, onNavigate }: SitePopupProps): ReactElement => {
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSuggestForm, setShowSuggestForm] = useState(false);
  const [suggestedChanges, setSuggestedChanges] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showAllEquipment, setShowAllEquipment] = useState(false);
  const [equipmentItems, setEquipmentItems] = useState<string[]>([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [hasMultipleEquipment, setHasMultipleEquipment] = useState(false);
  const [showSiteInfo, setShowSiteInfo] = useState(false);
  const [showTechnicalInfo, setShowTechnicalInfo] = useState(false);
  const [showFibreAccess, setShowFibreAccess] = useState(false);
  
  // Fibre change request modal state
  const [showFibreChangeForm, setShowFibreChangeForm] = useState(false);
  const [fibreChangeData, setFibreChangeData] = useState({
    joint_name: '',
    cable_name: '',
    section_number: '',
    elr: '',
    location: '',
    access: '',
    lat: '',
    lng: '',
    notes: '',
  });
  const [fibreSubmitting, setFibreSubmitting] = useState(false);
  const [fibreSubmitStatus, setFibreSubmitStatus] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Check if site has multiple equipment when the popup is opened
  useEffect(() => {
    // Debug log the site data, especially the access field
    console.log('SitePopup received site:', JSON.stringify({
      code: site.name.siteCode,
      type: site.name.siteType,
      access: site.access,
      site: site
    }, null, 2));
    
    const checkMultipleEquipment = async () => {
      if (!site.name.siteCode) return;
      
      // Only check for multiple equipment for SDH and PDH sites
      if (site.name.siteType === 'SDH' || site.name.siteType === 'PDH') {
        try {
          console.log(`Checking if ${site.name.siteType} site ${site.name.siteCode} has multiple equipment`);
          const hasMultiple = await hasSiteMultipleEquipment(site.name.siteCode, site.name.siteType);
          setHasMultipleEquipment(hasMultiple);
          console.log(`${site.name.siteType} site ${site.name.siteCode} has multiple equipment: ${hasMultiple}`);
          
          // If this site doesn't have multiple equipment, no need to run the other useEffect
          if (!hasMultiple) {
            setEquipmentItems([]);
          }
        } catch (error) {
          console.error('Error checking for multiple equipment:', error);
          setHasMultipleEquipment(false);
        }
      } else {
        // Reset state for other site types
        setHasMultipleEquipment(false);
        setEquipmentItems([]);
      }
    };
    
    checkMultipleEquipment();
  }, [site.name.siteCode, site.name.siteType]);
  
  // Debug for PDH sites in desktop view
  useEffect(() => {
    if (site.name.siteType === 'PDH' && site.pdhInfo && hasMultipleEquipment) {
      console.log(`PDH Desktop view debug - hasMultipleEquipment: ${hasMultipleEquipment}, isMobile: ${isMobile}`);
    }
  }, [site.name.siteType, site.pdhInfo, hasMultipleEquipment, isMobile]);
  
  // Load equipment when technical info is shown on mobile
  useEffect(() => {
    if (!showTechnicalInfo || !hasMultipleEquipment || equipmentItems.length > 0 || !isMobile) {
      return;
    }
    
    if (site.name.siteType !== 'SDH' && site.name.siteType !== 'PDH') {
      return;
    }
    
    const loadEquipment = async () => {
      setLoadingEquipment(true);
      try {
        console.log(`Loading equipment for ${site.name.siteType} site ${site.name.siteCode} on technical info expand`);
        const equipment = await fetchSiteEquipment(site.name.siteCode, site.name.siteType);
        console.log(`Loaded ${equipment.length} equipment items`);
        
        // Sort equipment items in numerical order
        const sortedEquipment = [...equipment].sort((a, b) => {
          // Extract device IDs if they exist
          const aMatch = a.match(/(\d+)#(\d+)/);
          const bMatch = b.match(/(\d+)#(\d+)/);
          
          if (aMatch && bMatch) {
            // If both have the pattern like "1511#01", compare base numbers first
            const aBase = parseInt(aMatch[1], 10);
            const bBase = parseInt(bMatch[1], 10);
            
            if (aBase !== bBase) {
              return aBase - bBase;
            }
            
            // If base numbers are the same, compare the numbers after #
            const aNumber = parseInt(aMatch[2], 10);
            const bNumber = parseInt(bMatch[2], 10);
            return aNumber - bNumber;
          }
          
          // Fallback to standard string comparison
          return a.localeCompare(b);
        });
        
        setEquipmentItems(sortedEquipment);
      } catch (error) {
        console.error('Error loading equipment:', error);
        setEquipmentItems([]);
      } finally {
        setLoadingEquipment(false);
      }
    };
    
    loadEquipment();
  }, [showTechnicalInfo, hasMultipleEquipment, equipmentItems.length, site.name.siteCode, site.name.siteType, isMobile]);
  
  // Prevent popup from closing when clicking inside the component
  const handlePopupClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  // Handle text area changes with propagation prevention
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    setSuggestedChanges(e.target.value);
  };
  
  // Set isMobile flag on component mount
  useEffect(() => {
    const mobileStatus = isMobileDevice();
    setIsMobile(mobileStatus);
    setIsIOS(isIOSDevice());
    console.log(`Device detection: isMobile=${mobileStatus}, browser=${navigator.userAgent}`);
    
    // Listen for site update events
    const handleSiteUpdated = (event: CustomEvent) => {
      const { siteCode } = event.detail;
      if (siteCode === site.name.siteCode) {
        console.log(`Site ${siteCode} was updated, reloading site data`);
        // Force parent component to reload the site
        if (onNavigate) {
          // Reload the current site
          window.location.reload();
        }
      }
    };
    
    window.addEventListener('site-updated', handleSiteUpdated as EventListener);
    
    return () => {
      window.removeEventListener('site-updated', handleSiteUpdated as EventListener);
    };
  }, [site.name.siteCode, site.coordinates.lat, site.coordinates.lng, onNavigate]);

  // Enforce hasMultipleEquipment check when isMobile changes 
  // This helps ensure proper rendering in different viewports
  useEffect(() => {
    if (site.name.siteType === 'PDH' && site.pdhInfo && site.name.siteCode) {
      console.log(`Re-checking PDH hasMultipleEquipment on isMobile change: ${isMobile}`);
      
      const recheckMultipleEquipment = async () => {
        try {
          const hasMultiple = await hasSiteMultipleEquipment(site.name.siteCode, 'PDH');
          setHasMultipleEquipment(hasMultiple);
          console.log(`Re-checked PDH site ${site.name.siteCode} has multiple equipment: ${hasMultiple}`);
        } catch (error) {
          console.error('Error re-checking for multiple equipment:', error);
        }
      };
      
      recheckMultipleEquipment();
    }
  }, [isMobile, site.name.siteType, site.pdhInfo, site.name.siteCode]);
  
  // Copy coordinates to clipboard with fallback for iOS
  const copyToClipboard = () => {
    if (!site.coordinates.lat || !site.coordinates.lng) return;
    
    const textToCopy = `${site.coordinates.lat}, ${site.coordinates.lng}`;
    
    // Create a fallback method for iOS
    const fallbackCopyTextToClipboard = (text: string) => {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea');
      textArea.value = text;
      
      // Make it non-visible
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      
      // Focus and select the text
      textArea.focus();
      textArea.select();
      
      let success = false;
      try {
        // Execute the copy command
        success = document.execCommand('copy');
      } catch (err) {
        console.error('Fallback: Could not copy text', err);
      }
      
      // Clean up
      document.body.removeChild(textArea);
      return success;
    };
    
    // Try using the Clipboard API first, fall back to the older method if it fails
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          // If Clipboard API fails, try the fallback
          const success = fallbackCopyTextToClipboard(textToCopy);
          if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }
        });
    } else {
      // Clipboard API not available, use fallback
      const success = fallbackCopyTextToClipboard(textToCopy);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };
  
  const handleSuggestChanges = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!user?.email || !suggestedChanges.trim()) return;
    
    try {
      setSubmitting(true);
      setSubmitStatus(null);
      
      await submitSiteChangeRequest({
        site_code: site.name.siteCode,
        user_email: user.email,
        suggested_changes: suggestedChanges,
        status: 'pending',
        station_name: site.name.stationName
      });
      
      setSubmitStatus({
        message: 'Your suggestion has been submitted for review!',
        type: 'success'
      });
      setSuggestedChanges('');
      
      // Auto-hide the form after successful submission
      setTimeout(() => {
        setShowSuggestForm(false);
        setSubmitStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting change request:', error);
      setSubmitStatus({
        message: 'Failed to submit your suggestion. Please try again.',
        type: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCancelSuggestion = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSuggestForm(false);
    setSuggestedChanges('');
    setSubmitStatus(null);
  };
  
  const handleNavigate = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Use Google Maps URL as fallback
    const { lat, lng } = site.coordinates;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  // Open fibre change modal with pre-filled current values
  const openFibreChangeForm = () => {
    setFibreChangeData({
      joint_name: site.name.stationName || '',
      cable_name: site.fibreInfo?.cableName || '',
      section_number: site.name.siteCode || '',
      elr: site.name.elrCode || '',
      location: site.fibreInfo?.location || '',
      access: site.access || '',
      lat: site.coordinates.lat.toString(),
      lng: site.coordinates.lng.toString(),
      notes: '',
    });
    setFibreSubmitStatus(null);
    setShowFibreChangeForm(true);
  };

  const handleFibreChangeSubmit = async () => {
    if (!user?.email || !site.fibreInfo?.id) return;

    setFibreSubmitting(true);
    setFibreSubmitStatus(null);
    try {
      await submitFibreChangeRequest({
        fibre_site_id: site.fibreInfo.id,
        original_joint_name: site.name.stationName || '',
        original_cable_name: site.fibreInfo.cableName || '',
        user_email: user.email,
        suggested_joint_name: fibreChangeData.joint_name || undefined,
        suggested_cable_name: fibreChangeData.cable_name || undefined,
        suggested_section_number: fibreChangeData.section_number || undefined,
        suggested_elr: fibreChangeData.elr || undefined,
        suggested_location: fibreChangeData.location || undefined,
        suggested_access: fibreChangeData.access || undefined,
        suggested_lat: fibreChangeData.lat ? parseFloat(fibreChangeData.lat) : undefined,
        suggested_lng: fibreChangeData.lng ? parseFloat(fibreChangeData.lng) : undefined,
        notes: fibreChangeData.notes || undefined,
        status: 'pending',
      });
      setFibreSubmitStatus({ message: 'Change request submitted successfully. An admin will review it shortly.', type: 'success' });
    } catch (err) {
      setFibreSubmitStatus({ message: err instanceof Error ? err.message : 'Failed to submit change request', type: 'error' });
    } finally {
      setFibreSubmitting(false);
    }
  };
  
  // Fetch equipment list when showAllEquipment is toggled to true (for desktop view)
  useEffect(() => {
    if (!showAllEquipment || !site.name.siteCode || !hasMultipleEquipment || isMobile) {
      return;
    }
    
    // Only fetch equipment for SDH and PDH sites
    if (site.name.siteType !== 'SDH' && site.name.siteType !== 'PDH') {
      return;
    }
    
    console.log(`Desktop fetchEquipment triggered: showAllEquipment=${showAllEquipment}, hasMultipleEquipment=${hasMultipleEquipment}, siteType=${site.name.siteType}`);
    
    const fetchEquipment = async () => {
      // Only fetch if we haven't already fetched
      if (equipmentItems.length === 0) {
        setLoadingEquipment(true);
        try {
          console.log(`Fetching equipment for ${site.name.siteType} site ${site.name.siteCode}`);
          const equipment = await fetchSiteEquipment(site.name.siteCode, site.name.siteType);
          console.log(`Fetched ${equipment.length} equipment items for ${site.name.siteType} site ${site.name.siteCode}`);
          
          // Sort equipment items in numerical order
          const sortedEquipment = [...equipment].sort((a, b) => {
            // Extract device IDs if they exist
            const aMatch = a.match(/(\d+)#(\d+)/);
            const bMatch = b.match(/(\d+)#(\d+)/);
            
            if (aMatch && bMatch) {
              // If both have the pattern like "1511#01", compare base numbers first
              const aBase = parseInt(aMatch[1], 10);
              const bBase = parseInt(bMatch[1], 10);
              
              if (aBase !== bBase) {
                return aBase - bBase;
              }
              
              // If base numbers are the same, compare the numbers after #
              const aNumber = parseInt(aMatch[2], 10);
              const bNumber = parseInt(bMatch[2], 10);
              return aNumber - bNumber;
            }
            
            // Fallback to standard string comparison
            return a.localeCompare(b);
          });
          
          setEquipmentItems(sortedEquipment);
        } catch (error) {
          console.error('Error fetching equipment:', error);
          setEquipmentItems([]);
        } finally {
          setLoadingEquipment(false);
        }
      } else {
        console.log(`Using cached equipment list (${equipmentItems.length} items)`);
      }
    };
    
    fetchEquipment();
  }, [showAllEquipment, site.name.siteCode, site.name.siteType, hasMultipleEquipment, equipmentItems.length, isMobile]);
  
  return (
    <PopupContainer onClick={handlePopupClick}>
      <HeaderSection siteType={site.name.siteType}>
        <SiteName>
          {(site.name.siteType === 'Signal' || site.name.siteType === 'Sub Signal' || site.name.siteType === 'Points')
            ? `${site.name.siteCode} ${site.name.siteType}`
            : site.name.siteType === 'Fibre' && site.fibreInfo
            ? site.fibreInfo.cableName
            : site.name.stationName}
        </SiteName>
        <SiteType>
          {site.batteryInfo ? 'UPS & Battery Site' : 
           site.name.siteType === 'SDH' ? 'SDH Site' : 
           site.name.siteType === 'PDH' ? 'PDH Site' : 
           site.name.siteType === 'LOC' ? 'Telecom LOC Site' : 
           site.name.siteType === 'Signal' || site.name.siteType === 'Sub Signal' ? 'Rail Signal' :
           site.name.siteType === 'Points' ? 'Rail Points' :
           site.name.siteType === 'Fibre' ? 'Fibre Cable Joint' :
           `${site.name.siteType} Site`}
        </SiteType>
      </HeaderSection>
      
      <BodySection>
        {/* Collapsible section for site info on mobile devices */}
        {isMobile ? (
          <>
            <CollapsibleSectionButton 
              expanded={showSiteInfo}
              onClick={() => setShowSiteInfo(!showSiteInfo)}
            >
              {showSiteInfo ? 'Hide Site Info' : 'Show Site Info'}
            </CollapsibleSectionButton>
            
            <CollapsibleContent expanded={showSiteInfo}>
              <InfoGrid>
                {/* For Fibre sites, show Joint Number instead of Site Code */}
                {site.name.siteType === 'Fibre' && site.name.stationName && (
                  <>
                    <InfoLabel>Joint Number</InfoLabel>
                    <InfoValue>{site.name.stationName}</InfoValue>
                  </>
                )}
                
                {/* Site info content here - hide site code for SPT and Fibre sites */}
                {site.name.siteCode && site.name.siteType !== 'Signal' && site.name.siteType !== 'Sub Signal' && site.name.siteType !== 'Points' && site.name.siteType !== 'Fibre' && (
                  <>
                    <InfoLabel>Site Code</InfoLabel>
                    <InfoValue>{site.name.siteCode}</InfoValue>
                  </>
                )}
                
                {/* Show "Calls into" for SPT sites */}
                {(site.name.siteType === 'Signal' || site.name.siteType === 'Sub Signal' || site.name.siteType === 'Points') && (
                  <>
                    <InfoLabel>Controlled by</InfoLabel>
                    <InfoValue>{site.name.stationName}</InfoValue>
                  </>
                )}
                
                {/* For Fibre sites, show ELR as "Location" */}
                {site.name.siteType === 'Fibre' && site.name.elrCode && (
                  <>
                    <InfoLabel>Location</InfoLabel>
                    <InfoValue>{site.name.elrCode}</InfoValue>
                  </>
                )}
                
                {/* ELR Code for non-fibre sites */}
                {site.name.siteType !== 'Fibre' && site.name.elrCode && (
                  <>
                    <InfoLabel>ELR Code</InfoLabel>
                    <InfoValue>{site.name.elrCode}</InfoValue>
                  </>
                )}
                
                {((site.batteryInfo && site.batteryInfo.mileage > 0) || 
                  (site.sdhInfo && site.sdhInfo.mileage > 0) || 
                  (site.pdhInfo && site.pdhInfo.mileage > 0) || 
                  (site.locInfo && site.locInfo.mileage > 0)) && (
                  <>
                    <InfoLabel>Mileage</InfoLabel>
                    <InfoValue>
                      {site.batteryInfo?.mileage || site.sdhInfo?.mileage || site.pdhInfo?.mileage || site.locInfo?.mileage}
                    </InfoValue>
                  </>
                )}
                
                {/* Expandable access for Fibre sites */}
                {site.name.siteType === 'Fibre' ? (
                  <>
                    <InfoLabel>Access</InfoLabel>
                    <InfoValue>
                      <EquipmentButton
                        expanded={showFibreAccess}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowFibreAccess(!showFibreAccess);
                        }}
                      >
                        {showFibreAccess ? 'Hide Access Info' : 'Show Access Info'}
                      </EquipmentButton>
                      {showFibreAccess && (
                        <div style={{ marginTop: '6px', fontSize: '13px', lineHeight: '1.4', color: '#ccc' }}>
                          {site.access || 'Not specified'}
                        </div>
                      )}
                    </InfoValue>
                  </>
                ) : (
                  <>
                    <InfoLabel>Access</InfoLabel>
                    <InfoValue>
                      <AccessContainer>
                        {site.access || 'Not specified'}
                        <SuggestAccessButton 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowSuggestForm(true);
                          }}
                          title="Suggest access requirements for this site"
                        />
                      </AccessContainer>
                    </InfoValue>
                  </>
                )}
                
                <InfoLabel>Coordinates</InfoLabel>
                <InfoValue>
                  <CoordinatesBadge 
                    className={copied ? 'copied' : ''} 
                    onClick={copyToClipboard} 
                    title="Click to copy coordinates"
                  >
                    {copied ? <CopySuccessIcon /> : <CopyIcon />}
                    {site.coordinates.lat.toFixed(6)}, {site.coordinates.lng.toFixed(6)}
                  </CoordinatesBadge>
                </InfoValue>
              </InfoGrid>
            </CollapsibleContent>
            
            <CollapsibleSectionButton 
              expanded={showTechnicalInfo}
              onClick={() => setShowTechnicalInfo(!showTechnicalInfo)}
            >
              {showTechnicalInfo ? 'Hide Technical Info' : 'Show Technical Info'}
            </CollapsibleSectionButton>
            
            <CollapsibleContent expanded={showTechnicalInfo}>
              <InfoGrid>
                {/* Technical info based on site type */}
                {site.batteryInfo && (
                  <>
                    <InfoLabel>Battery Model</InfoLabel>
                    <InfoValue>{site.batteryInfo.batteryModel || 'Not specified'}</InfoValue>
                    
                    <InfoLabel>Equipment</InfoLabel>
                    <InfoValue>{site.batteryInfo.item || 'Not specified'}</InfoValue>
                    
                    {(site.batteryInfo.noCellStrings > 0 || site.batteryInfo.noCells > 0) && (
                      <>
                        <InfoLabel>Configuration</InfoLabel>
                        <InfoValue>
                          {site.batteryInfo.noCellStrings > 0 ? `${site.batteryInfo.noCellStrings} string(s)` : ''}
                          {site.batteryInfo.noCellStrings > 0 && site.batteryInfo.noCells > 0 ? ', ' : ''}
                          {site.batteryInfo.noCells > 0 ? `${site.batteryInfo.noCells} cell(s)` : ''}
                        </InfoValue>
                      </>
                    )}
                    
                    {site.batteryInfo.supEquip && (
                      <>
                        <InfoLabel>Supported Equipment</InfoLabel>
                        <InfoValue>{site.batteryInfo.supEquip}</InfoValue>
                      </>
                    )}
                  </>
                )}
                
                {site.name.siteType === 'SDH' && site.sdhInfo && (
                  <>
                    {site.sdhInfo.nodeType && (
                      <>
                        <InfoLabel>Node Type</InfoLabel>
                        <InfoValue>{site.sdhInfo.nodeType}</InfoValue>
                      </>
                    )}
                    
                    {/* Show additional equipment toggle button if this site has multiple equipment */}
                    {hasMultipleEquipment && (
                      <>
                        <InfoLabel>Equipment</InfoLabel>
                        <InfoValue>
                          <EquipmentButton 
                            expanded={showAllEquipment}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowAllEquipment(!showAllEquipment);
                            }}
                          >
                            {showAllEquipment ? 'Hide all MUXs' : 'Show all MUXs'}
                          </EquipmentButton>
                          
                          {/* Show equipment list only when expanded */}
                          {showAllEquipment && (
                            <>
                              {loadingEquipment ? (
                                <LoadingIndicator>Loading equipment...</LoadingIndicator>
                              ) : (
                                equipmentItems.length > 0 ? (
                                  <EquipmentList>
                                    {equipmentItems.map((equipment, index) => (
                                      <EquipmentItem key={index}>{equipment}</EquipmentItem>
                                    ))}
                                  </EquipmentList>
                                ) : (
                                  <div>No additional equipment found</div>
                                )
                              )}
                            </>
                          )}
                        </InfoValue>
                      </>
                    )}
                  </>
                )}
                
                {site.name.siteType === 'PDH' && site.pdhInfo && (
                  <>
                    {/* Show additional equipment toggle button if this site has multiple equipment */}
                    {hasMultipleEquipment && (
                      <>
                        <InfoLabel>Equipment</InfoLabel>
                        <InfoValue>
                          <EquipmentButton 
                            expanded={showAllEquipment}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowAllEquipment(!showAllEquipment);
                            }}
                          >
                            {showAllEquipment ? 'Hide all MUXs' : 'Show all MUXs'}
                          </EquipmentButton>
                          
                          {/* Show equipment list only when expanded */}
                          {showAllEquipment && (
                            <>
                              {loadingEquipment ? (
                                <LoadingIndicator>Loading equipment...</LoadingIndicator>
                              ) : (
                                equipmentItems.length > 0 ? (
                                  <EquipmentList>
                                    {equipmentItems.map((equipment, index) => (
                                      <EquipmentItem key={index}>{equipment}</EquipmentItem>
                                    ))}
                                  </EquipmentList>
                                ) : (
                                  <div>No additional equipment found</div>
                                )
                              )}
                            </>
                          )}
                        </InfoValue>
                      </>
                    )}
                  </>
                )}
                
                {site.name.siteType === 'LOC' && site.locInfo && (
                  <>
                    {site.locInfo.item && (
                      <>
                        <InfoLabel>Item</InfoLabel>
                        <InfoValue>{site.locInfo.item}</InfoValue>
                      </>
                    )}
                    
                    {site.locInfo.locId && (
                      <>
                        <InfoLabel>LOC ID</InfoLabel>
                        <InfoValue>{site.locInfo.locId}</InfoValue>
                      </>
                    )}
                  </>
                )}
              </InfoGrid>
            </CollapsibleContent>
          </>
        ) : (
          // For desktop, keep the existing layout with updated text
          <InfoGrid>
            {/* For battery sites, display information in the specific order requested */}
            {site.batteryInfo && (
              <>
                {/* Always show site code if available */}
                {site.name.siteCode && (
                  <>
                    <InfoLabel>Site Code</InfoLabel>
                    <InfoValue>{site.name.siteCode}</InfoValue>
                  </>
                )}
                
                {/* Always show ELR code if available */}
                {site.name.elrCode && (
                  <>
                    <InfoLabel>ELR Code</InfoLabel>
                    <InfoValue>{site.name.elrCode}</InfoValue>
                  </>
                )}
                
                {/* Show mileage for battery sites if available */}
                {site.batteryInfo.mileage > 0 && (
                  <>
                    <InfoLabel>Mileage</InfoLabel>
                    <InfoValue>{site.batteryInfo.mileage}</InfoValue>
                  </>
                )}
                
                {/* Show battery model */}
                <InfoLabel>Battery Model</InfoLabel>
                <InfoValue>{site.batteryInfo.batteryModel || 'Not specified'}</InfoValue>
                
                {/* Show equipment */}
                <InfoLabel>Equipment</InfoLabel>
                <InfoValue>{site.batteryInfo.item || 'Not specified'}</InfoValue>
                
                {/* Show configuration */}
                {(site.batteryInfo.noCellStrings > 0 || site.batteryInfo.noCells > 0) && (
                  <>
                    <InfoLabel>Configuration</InfoLabel>
                    <InfoValue>
                      {site.batteryInfo.noCellStrings > 0 ? `${site.batteryInfo.noCellStrings} string(s)` : ''}
                      {site.batteryInfo.noCellStrings > 0 && site.batteryInfo.noCells > 0 ? ', ' : ''}
                      {site.batteryInfo.noCells > 0 ? `${site.batteryInfo.noCells} cell(s)` : ''}
                    </InfoValue>
                  </>
                )}
                
                {/* Show supported equipment */}
                {site.batteryInfo.supEquip && (
                  <>
                    <InfoLabel>Supported Equipment</InfoLabel>
                    <InfoValue>{site.batteryInfo.supEquip}</InfoValue>
                  </>
                )}
                
                {/* Show access information */}
                <InfoLabel>Access</InfoLabel>
                <InfoValue>
                  <AccessContainer>
                    {site.access || 'Not specified'}
                    <SuggestAccessButton 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowSuggestForm(true);
                      }}
                      title="Suggest access requirements for this site"
                    />
                  </AccessContainer>
                </InfoValue>
                
                {/* Show coordinates */}
                <InfoLabel>Coordinates</InfoLabel>
                <InfoValue>
                  <CoordinatesBadge 
                    className={copied ? 'copied' : ''} 
                    onClick={copyToClipboard} 
                    title="Click to copy coordinates"
                  >
                    {copied ? <CopySuccessIcon /> : <CopyIcon />}
                    {site.coordinates.lat.toFixed(6)}, {site.coordinates.lng.toFixed(6)}
                  </CoordinatesBadge>
                </InfoValue>
              </>
            )}
            
            {/* For non-battery sites, keep the original format */}
            {!site.batteryInfo && (
              <>
                {/* For Fibre sites, show Joint Number instead of Site Code */}
                {site.name.siteType === 'Fibre' && site.name.stationName && (
                  <>
                    <InfoLabel>Joint Number</InfoLabel>
                    <InfoValue>{site.name.stationName}</InfoValue>
                  </>
                )}
                
                {/* Show site code if available - hide for SPT and Fibre sites */}
                {site.name.siteCode && site.name.siteType !== 'Signal' && site.name.siteType !== 'Sub Signal' && site.name.siteType !== 'Points' && site.name.siteType !== 'Fibre' && (
                  <>
                    <InfoLabel>Site Code</InfoLabel>
                    <InfoValue>{site.name.siteCode}</InfoValue>
                  </>
                )}
                
                {/* Show ELR as "Location" for Fibre, "ELR Code" for others */}
                {site.name.elrCode && (
                  <>
                    <InfoLabel>{site.name.siteType === 'Fibre' ? 'Location' : 'ELR Code'}</InfoLabel>
                    <InfoValue>{site.name.elrCode}</InfoValue>
                  </>
                )}
                
                {/* Show site mileage for all site types */}
                {((site.sdhInfo && site.sdhInfo.mileage > 0) || 
                  (site.pdhInfo && site.pdhInfo.mileage > 0) || 
                  (site.locInfo && site.locInfo.mileage > 0)) && (
                  <>
                    <InfoLabel>Mileage</InfoLabel>
                    <InfoValue>
                      {site.sdhInfo?.mileage || site.pdhInfo?.mileage || site.locInfo?.mileage}
                    </InfoValue>
                  </>
                )}
                
                {/* Show SDH-specific information */}
                {site.name.siteType === 'SDH' && site.sdhInfo && (
                  <>
                    {site.sdhInfo.nodeType && (
                      <>
                        <InfoLabel>Node Type</InfoLabel>
                        <InfoValue>{site.sdhInfo.nodeType}</InfoValue>
                      </>
                    )}
                    
                    {/* Show additional equipment toggle button if this site has multiple equipment */}
                    {hasMultipleEquipment && (
                      <>
                        <InfoLabel>Equipment</InfoLabel>
                        <InfoValue>
                          <EquipmentButton 
                            expanded={showAllEquipment}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowAllEquipment(!showAllEquipment);
                            }}
                          >
                            {showAllEquipment ? 'Hide all MUXs' : 'Show all MUXs'}
                          </EquipmentButton>
                          
                          {/* Show equipment list only when expanded */}
                          {showAllEquipment && (
                            <>
                              {loadingEquipment ? (
                                <LoadingIndicator>Loading equipment...</LoadingIndicator>
                              ) : (
                                equipmentItems.length > 0 ? (
                                  <EquipmentList>
                                    {equipmentItems.map((equipment, index) => (
                                      <EquipmentItem key={index}>{equipment}</EquipmentItem>
                                    ))}
                                  </EquipmentList>
                                ) : (
                                  <div>No additional equipment found</div>
                                )
                              )}
                            </>
                          )}
                        </InfoValue>
                      </>
                    )}
                  </>
                )}
                
                {/* Show PDH-specific information */}
                {site.name.siteType === 'PDH' && site.pdhInfo && (
                  <>
                    {/* Show additional equipment toggle button if this site has multiple equipment */}
                    {hasMultipleEquipment && (
                      <>
                        <InfoLabel>Equipment</InfoLabel>
                        <InfoValue>
                          <EquipmentButton 
                            expanded={showAllEquipment}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowAllEquipment(!showAllEquipment);
                            }}
                          >
                            {showAllEquipment ? 'Hide all MUXs' : 'Show all MUXs'}
                          </EquipmentButton>
                          
                          {/* Show equipment list only when expanded */}
                          {showAllEquipment && (
                            <>
                              {loadingEquipment ? (
                                <LoadingIndicator>Loading equipment...</LoadingIndicator>
                              ) : (
                                equipmentItems.length > 0 ? (
                                  <EquipmentList>
                                    {equipmentItems.map((equipment, index) => (
                                      <EquipmentItem key={index}>{equipment}</EquipmentItem>
                                    ))}
                                  </EquipmentList>
                                ) : (
                                  <div>No additional equipment found</div>
                                )
                              )}
                            </>
                          )}
                        </InfoValue>
                      </>
                    )}
                  </>
                )}
                
                {/* Show SPT-specific information */}
                {(site.name.siteType === 'Signal' || site.name.siteType === 'Sub Signal' || site.name.siteType === 'Points') && (
                  <>
                    <InfoLabel>Controlled by</InfoLabel>
                    <InfoValue>{site.name.stationName}</InfoValue>
                  </>
                )}
                
                {/* Show LOC-specific information */}
                {site.name.siteType === 'LOC' && site.locInfo && (
                  <>
                    {site.locInfo.item && (
                      <>
                        <InfoLabel>Item</InfoLabel>
                        <InfoValue>{site.locInfo.item}</InfoValue>
                      </>
                    )}
                    
                    {site.locInfo.locId && (
                      <>
                        <InfoLabel>LOC ID</InfoLabel>
                        <InfoValue>{site.locInfo.locId}</InfoValue>
                      </>
                    )}
                  </>
                )}
                
                {/* Show access information for non-battery sites */}
                {site.name.siteType === 'Fibre' ? (
                  <>
                    <InfoLabel>Access</InfoLabel>
                    <InfoValue>
                      <EquipmentButton
                        expanded={showFibreAccess}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowFibreAccess(!showFibreAccess);
                        }}
                      >
                        {showFibreAccess ? 'Hide Access Info' : 'Show Access Info'}
                      </EquipmentButton>
                      {showFibreAccess && (
                        <div style={{ marginTop: '6px', fontSize: '13px', lineHeight: '1.4', color: '#ccc' }}>
                          {site.access || 'Not specified'}
                        </div>
                      )}
                    </InfoValue>
                  </>
                ) : (
                  <>
                    <InfoLabel>Access</InfoLabel>
                    <InfoValue>
                      <AccessContainer>
                        {site.access || 'Not specified'}
                        <SuggestAccessButton 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowSuggestForm(true);
                          }}
                          title="Suggest access requirements for this site"
                        />
                      </AccessContainer>
                    </InfoValue>
                  </>
                )}
                
                {/* Show coordinates for non-battery sites */}
                <InfoLabel>Coordinates</InfoLabel>
                <InfoValue>
                  <CoordinatesBadge 
                    className={copied ? 'copied' : ''} 
                    onClick={copyToClipboard} 
                    title="Click to copy coordinates"
                  >
                    {copied ? <CopySuccessIcon /> : <CopyIcon />}
                    {site.coordinates.lat.toFixed(6)}, {site.coordinates.lng.toFixed(6)}
                  </CoordinatesBadge>
                </InfoValue>
              </>
            )}
          </InfoGrid>
        )}
        
        {showSuggestForm && (
          <AccessSuggestionForm>
            <TextArea
              placeholder="Describe the access requirements for this site..."
              value={suggestedChanges}
              onChange={handleTextAreaChange}
              disabled={submitting}
              onClick={handlePopupClick}
            />
            
            {submitStatus && (
              <StatusMessage type={submitStatus.type}>
                {submitStatus.message}
              </StatusMessage>
            )}
            
            <FormButtonContainer>
              <FormButton
                variant="primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSuggestChanges(e);
                }}
                disabled={!suggestedChanges.trim() || submitting}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </FormButton>
              <FormButton
                variant="secondary"
                onClick={handleCancelSuggestion}
                disabled={submitting}
              >
                Cancel
              </FormButton>
            </FormButtonContainer>
          </AccessSuggestionForm>
        )}
        
        {/* Suggest a Change button for Fibre sites */}
        {site.name.siteType === 'Fibre' && site.fibreInfo?.id && (
          <div style={{ marginTop: '12px' }}>
            <NavigateButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openFibreChangeForm();
              }}
              style={{ background: '#00838f' }}
            >
              Suggest a Change
            </NavigateButton>
          </div>
        )}

        {/* Fibre Change Request Modal */}
        {showFibreChangeForm && site.name.siteType === 'Fibre' && (
          <div style={{
            marginTop: '12px',
            background: '#1a1a1a',
            borderRadius: '8px',
            border: '1px solid rgba(0,188,212,0.3)',
            padding: '14px',
          }}>
            <div style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '10px', color: '#00bcd4' }}>
              Suggest a Change
            </div>
            
            {fibreSubmitStatus?.type === 'success' ? (
              <div style={{ color: '#4ade80', fontSize: '13px', marginBottom: '8px' }}>
                {fibreSubmitStatus.message}
              </div>
            ) : (
              <>
                {fibreSubmitStatus?.type === 'error' && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginBottom: '8px' }}>
                    {fibreSubmitStatus.message}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#999', marginBottom: '3px' }}>Joint Name</label>
                    <input type="text" value={fibreChangeData.joint_name}
                      onChange={(e) => setFibreChangeData(d => ({ ...d, joint_name: e.target.value }))}
                      style={{ width: '100%', padding: '6px', background: '#333', border: '1px solid #444', borderRadius: '4px', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#999', marginBottom: '3px' }}>Cable Name</label>
                    <input type="text" value={fibreChangeData.cable_name}
                      onChange={(e) => setFibreChangeData(d => ({ ...d, cable_name: e.target.value }))}
                      style={{ width: '100%', padding: '6px', background: '#333', border: '1px solid #444', borderRadius: '4px', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#999', marginBottom: '3px' }}>Section Number</label>
                    <input type="text" value={fibreChangeData.section_number}
                      onChange={(e) => setFibreChangeData(d => ({ ...d, section_number: e.target.value }))}
                      style={{ width: '100%', padding: '6px', background: '#333', border: '1px solid #444', borderRadius: '4px', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#999', marginBottom: '3px' }}>ELR</label>
                    <input type="text" value={fibreChangeData.elr}
                      onChange={(e) => setFibreChangeData(d => ({ ...d, elr: e.target.value }))}
                      style={{ width: '100%', padding: '6px', background: '#333', border: '1px solid #444', borderRadius: '4px', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#999', marginBottom: '3px' }}>Location</label>
                    <input type="text" value={fibreChangeData.location}
                      onChange={(e) => setFibreChangeData(d => ({ ...d, location: e.target.value }))}
                      style={{ width: '100%', padding: '6px', background: '#333', border: '1px solid #444', borderRadius: '4px', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#999', marginBottom: '3px' }}>Access</label>
                    <input type="text" value={fibreChangeData.access}
                      onChange={(e) => setFibreChangeData(d => ({ ...d, access: e.target.value }))}
                      style={{ width: '100%', padding: '6px', background: '#333', border: '1px solid #444', borderRadius: '4px', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#999', marginBottom: '3px' }}>Latitude</label>
                    <input type="text" value={fibreChangeData.lat}
                      onChange={(e) => setFibreChangeData(d => ({ ...d, lat: e.target.value }))}
                      style={{ width: '100%', padding: '6px', background: '#333', border: '1px solid #444', borderRadius: '4px', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#999', marginBottom: '3px' }}>Longitude</label>
                    <input type="text" value={fibreChangeData.lng}
                      onChange={(e) => setFibreChangeData(d => ({ ...d, lng: e.target.value }))}
                      style={{ width: '100%', padding: '6px', background: '#333', border: '1px solid #444', borderRadius: '4px', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'block', fontSize: '11px', color: '#999', marginBottom: '3px' }}>Notes (optional)</label>
                  <textarea value={fibreChangeData.notes}
                    onChange={(e) => setFibreChangeData(d => ({ ...d, notes: e.target.value }))}
                    rows={2}
                    style={{ width: '100%', padding: '6px', background: '#333', border: '1px solid #444', borderRadius: '4px', color: '#fff', fontSize: '12px', resize: 'vertical', boxSizing: 'border-box' }}
                    placeholder="Reason for change..."
                  />
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowFibreChangeForm(false)}
                    style={{ padding: '6px 14px', background: '#444', border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer', fontSize: '12px' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleFibreChangeSubmit(); }}
                    disabled={fibreSubmitting}
                    style={{ padding: '6px 14px', background: '#00838f', border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer', fontSize: '12px', opacity: fibreSubmitting ? 0.6 : 1 }}
                  >
                    {fibreSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <NavigateButtonContainer>
          {site.name.siteType !== 'Fibre' && (
            <NavigateButton
              onClick={handleNavigate}
            >
              Navigate to Site
            </NavigateButton>
          )}
          
          {/* Debug reload button */}
          <div style={{ marginTop: '10px', textAlign: 'center' }}>
            <button 
              onClick={() => window.location.reload()} 
              style={{ 
                background: '#444', 
                border: 'none', 
                color: '#fff', 
                padding: '8px 16px', 
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Refresh Site Data
            </button>
          </div>
        </NavigateButtonContainer>
      </BodySection>
    </PopupContainer>
  );
};